import { useState, useEffect, type JSX } from 'react';
import { Document, useMedplum } from '@medplum/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, Group, Paper, Stack, Text, Title, Loader, Box } from '@mantine/core';
import { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { DocumentService } from '../../services/DocumentService';
import { useDocumentsContext } from '../../contexts/DocumentsContext';

/**
 * Component for displaying and handling consent form agreements
 */
interface ConsentFormProps {
  formId?: string;
  embedded?: boolean;
  onComplete?: () => void;
}

export function ConsentForm({ formId: propFormId, embedded, onComplete }: ConsentFormProps = {}): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshDocuments } = useDocumentsContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('Consent Form');
  const [agreed, setAgreed] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(null);
  
  // Get form ID from props or URL query parameter
  const params = new URLSearchParams(location.search);
  const formId = propFormId || params.get('id');
  
  useEffect(() => {
    async function loadConsentForm() {
      setLoading(true);
      setError(null);
      
      try {
        if (!formId) {
          // No form ID specified, use default consent text
          setContent(defaultConsentText);
          setLoading(false);
          return;
        }
        
        // Load the form using DocumentService
        const { response, questionnaire } = await DocumentService.getFormById(medplum, formId);
        setQuestionnaireResponse(response);
        
        // Handle Questionnaire resource
        if (questionnaire && questionnaire.resourceType === 'Questionnaire') {
          // Questionnaire-based consent form handling
          setTitle(questionnaire.title || 'Consent Form');
          
          // Look for content in the item array with linkId 'content'
          const contentItem = questionnaire.item?.find(item => item.linkId === 'content');
          
          if (contentItem) {
            // Extract HTML content from rendering-xhtml extension
            const htmlExt = contentItem.extension?.find(
              ext => ext.url === 'http://hl7.org/fhir/StructureDefinition/rendering-xhtml'
            );
            
            if (htmlExt && htmlExt.valueString) {
              setContent(htmlExt.valueString);
            } else {
              setContent(defaultConsentText);
              setError('Questionnaire has no HTML content - using default text');
            }
          } else {
            // Fall back to description or default text
            setContent(questionnaire.description || defaultConsentText);
            setError('Questionnaire has no content item - using default text');
          }
        } else {
          setContent(defaultConsentText);
          setError('No questionnaire resource found or not a supported type - using default text');
        }
        
        // Check if already signed
        if (response.status === 'completed') {
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setContent(defaultConsentText);
        setError('Error loading form data - using default text');
      } finally {
        setLoading(false);
      }
    }
    
    loadConsentForm();
  }, [medplum, formId]);
  
  async function handleSubmit() {
    if (!agreed) return;
    
    try {
      // If we have an existing QuestionnaireResponse, update it
      if (questionnaireResponse && questionnaireResponse.id) {
        // Update with agreed status
        const updatedResponse = {
          ...questionnaireResponse,
          status: 'completed' as const,
          // Add signature metadata
          item: [
            {
              linkId: 'agreement',
              text: 'Client Agreement',
              answer: [
                {
                  valueBoolean: true
                }
              ]
            },
            {
              linkId: 'signature-datetime',
              text: 'Signed On',
              answer: [
                {
                  valueDateTime: new Date().toISOString()
                }
              ]
            }
          ]
        };
        
        await medplum.updateResource(updatedResponse);
      }
      
      setSubmitted(true);
      window.scrollTo(0, 0);
      
      // Refresh document list
      refreshDocuments();
      
      // If this is embedded and has a callback, use that
      if (embedded && onComplete) {
        onComplete();
      } else {
        // Otherwise, redirect back to documents after a delay
        setTimeout(() => {
          navigate('/documents');
        }, 2000);
      }
    } catch (err) {
      console.error('Error submitting agreement:', err);
      setError('Error saving your agreement. Please try again.');
    }
  }
  
  if (loading) {
    return (
      <Box p="xl" style={{ display: 'flex', justifyContent: 'center' }}>
        <Loader size="lg" />
      </Box>
    );
  }
  
  // The main content to render
  const mainContent = (
    <>
      {error && (
        <Alert color="yellow" mb="md">
          {error}
        </Alert>
      )}
      
      {submitted ? (
        <Stack align="center" gap="md">
          <Title order={2}>Thank You</Title>
          <Text>Your consent has been recorded.</Text>
          {!embedded && (
            <Button onClick={() => navigate('/documents')}>
              Back to Documents
            </Button>
          )}
        </Stack>
      ) : (
        <Stack gap="lg">
          <Title order={embedded ? 3 : 2}>{title}</Title>
          
          <Paper p="md" withBorder style={{ maxHeight: embedded ? '300px' : '400px', overflow: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </Paper>
          
          <Stack gap="md">
            <Box>
              <Checkbox 
                checked={agreed}
                onChange={(e) => setAgreed(e.currentTarget.checked)}
                label="I have read and agree to the terms above"
              />
            </Box>
            
            <Button 
              onClick={handleSubmit}
              disabled={!agreed}
              style={{ width: '200px' }}
            >
              Submit Agreement
            </Button>
          </Stack>
        </Stack>
      )}
    </>
  );
  
  // If embedded, don't wrap in Document component
  if (embedded) {
    return mainContent;
  }
  
  // Otherwise use the standard Document wrapper
  return (
    <Document width={800}>
      {mainContent}
    </Document>
  );
}

/**
 * Default consent text as fallback
 */
const defaultConsentText = `
<h3>INFORMED CONSENT FOR THERAPY SERVICES</h3>
<p>Welcome to SecureHealth. This document contains important information about our professional services and business policies. Please read it carefully and make note of any questions you might have.</p>

<h4>THERAPY SERVICES</h4>
<p>Psychotherapy is not easily described in general statements. It varies depending on the personalities of the therapist and client, and the particular problems you hope to address. There are many different methods we may use to deal with those problems. Psychotherapy is not like a medical doctor visit. Instead, it calls for a very active effort on your part.</p>

<h4>CONFIDENTIALITY</h4>
<p>The law protects the privacy of all communications between a client and a therapist. In most situations, we can only release information about your treatment to others if you sign a written authorization form. There are some situations in which we are legally obligated to take action to protect others from harm, even if we have to reveal some information about a client's treatment.</p>

<h4>PROFESSIONAL RECORDS</h4>
<p>The laws and standards of our profession require that we keep Protected Health Information about you in your Clinical Record. You may examine and/or receive a copy of your Clinical Record if you request it in writing.</p>

<h4>PATIENT RIGHTS</h4>
<p>HIPAA provides you with several new or expanded rights with regard to your Clinical Record and disclosures of protected health information. These rights include requesting that we amend your record; requesting restrictions on what information from your Clinical Record is disclosed to others; requesting an accounting of most disclosures of protected health information that you have neither consented to nor authorized.</p>

<p>By checking the box below, I indicate my agreement with the terms outlined above.</p>
`;